/**
 * Pure handlers behind the story strip's buttons, so their guards are unit-tested without a DOM.
 *
 * - `retrySend`: "Try again" / "Ask what happened" SEND a message (UX map C21), but never around the composer's own guards.
 * - `makeCancel`: "Cancel task" with a second-click guard and the refusal copy.
 */
import { describeLaneV2Error, isLaneV2Error } from "./api";

export type RetryResult = "sent" | "filled" | "skipped";

export interface RetryComposer {
  /** A turn is running or a send is in flight on the session. */
  inFlight: boolean;
  offline: boolean;
  /** Start a turn with this text ONLY: no composer attachments, no context chip, never the running turn's inbox. */
  sendTextOnly: (text: string) => Promise<boolean>;
}

export interface RetryDeps {
  /** The route's send-in-flight flag (shared with the composer's own submit). */
  busyRef: { current: boolean };
  /** Is the composer's text box empty? A typed draft is never overwritten. */
  inputEmpty: () => boolean;
  fill: (text: string) => void;
}

/**
 * Re-send `text` unless the composer is busy. Busy (a send in flight, a turn running, offline) or a refused send → the text is
 * put in the composer instead, and only when the box is empty ("skipped" otherwise: the typed draft wins).
 */
export async function retrySend(composer: RetryComposer, text: string, deps: RetryDeps): Promise<RetryResult> {
  const fillIfEmpty = (): RetryResult => {
    if (!deps.inputEmpty()) return "skipped";
    deps.fill(text);
    return "filled";
  };
  if (deps.busyRef.current || composer.inFlight || composer.offline) return fillIfEmpty();
  deps.busyRef.current = true;
  try {
    return (await composer.sendTextOnly(text)) ? "sent" : fillIfEmpty();
  } catch {
    return fillIfEmpty();
  } finally {
    deps.busyRef.current = false;
  }
}

/** The question "Ask what happened" sends: it names the request it is about so the server answers about the right turn. */
export function askWhatHappenedText(requestTitle: string | null | undefined): string {
  const t = (requestTitle ?? "").replace(/\s+/g, " ").trim();
  const short = t.length > 80 ? `${t.slice(0, 79)}…` : t;
  return short
    ? `What went wrong with my request “${short}”, and what are my options from here?`
    : "What went wrong with my last request, and what are my options from here?";
}

/**
 * The cancel handler: ignores a click while one is running; `run` resolves to the refusal copy (or null when cancelled);
 * a throw becomes a fixed line. `setMessage` receives the copy to show.
 */
export function makeCancel(args: {
  run: () => Promise<string | null>;
  busyRef: { current: boolean };
  setBusy: (busy: boolean) => void;
  setMessage: (message: string | null) => void;
}): () => Promise<void> {
  return async () => {
    if (args.busyRef.current) return;
    args.busyRef.current = true;
    args.setBusy(true);
    args.setMessage(null);
    try {
      args.setMessage(await args.run());
    } catch {
      args.setMessage("The task could not be cancelled. Nothing was changed.");
    } finally {
      args.busyRef.current = false;
      args.setBusy(false);
    }
  };
}

// ── Pause / Resume (UX map B1, D3): the buttons come from R3 `task_controls` ────────────────────────────────────────

export type TaskControlKind = "hold" | "resume";

/** The copy for a 200 whose typed `status` says nothing happened (or that it is under way); null = nothing to add. */
export function taskControlStatusCopy(kind: TaskControlKind, status: string): string | null {
  if (kind === "hold") {
    if (status === "requested") return null;
    if (status === "already_pending") return "Already pausing after this component.";
    return "This task can't be paused right now. It may have moved on.";
  }
  if (status === "resumed") return null;
  return "This task is no longer paused.";
}

/**
 * The Pause / Resume handler: a click while one is running is ignored (the same guard as `makeCancel`); `run` resolves to the
 * typed status or throws (its copy is `taskControlFailureCopy`). The message is the refusal copy, or null when it worked.
 */
export function makeTaskControl(args: {
  kind: TaskControlKind;
  run: () => Promise<{ status: string }>;
  busyRef: { current: boolean };
  setBusy: (busy: boolean) => void;
  setMessage: (message: string | null) => void;
}): () => Promise<void> {
  return async () => {
    if (args.busyRef.current) return;
    args.busyRef.current = true;
    args.setBusy(true);
    args.setMessage(null);
    try {
      const out = await args.run();
      args.setMessage(taskControlStatusCopy(args.kind, out.status));
    } catch (err) {
      args.setMessage(taskControlFailureCopy(args.kind, err));
    } finally {
      args.busyRef.current = false;
      args.setBusy(false);
    }
  };
}

/** Words for a failed Pause / Resume: a typed refusal in its own copy (`HOLD_BUDGET_STILL_EXHAUSTED`, `STALE_TRANSITION`, offline...), anything else a fixed line. */
export function taskControlFailureCopy(kind: TaskControlKind, err: unknown): string {
  if (isLaneV2Error(err)) return describeLaneV2Error(err);
  return kind === "hold" ? "The task could not be paused. Nothing was changed." : "The task could not be resumed. Nothing was changed.";
}
